import { Inject, Injectable } from '@angular/core';
import { CompetitionRepository } from '../infrastructure/repositories/competition.repository';
import { forkJoin, Observable, of } from 'rxjs';
import { Competition } from '../model/interfaces/competition';
import { map } from 'rxjs/operators';
import { allPrizeWinners } from './prizeWinnersData';
import { SimpleCompetitor } from '../../+shared/model/interfaces/simple-competitor';
import { CompetitionWinnersContentResponse } from '../model/api/competition-winners-content-response';
import { CompetitionWinner } from '../model/interfaces/competition-winner';
import { CompetitionPrize } from '../model/interfaces/competition-prize';
import { CompetitionPrizeWinnersGroup } from '../model/interfaces/competition-prize-winners-group';
import { PriceWinners } from '../model/interfaces/prize-winners';
import { DOCUMENT } from '@angular/common';
declare var ddkCompetitionsPrizeWinners;

@Injectable({
    providedIn: 'root'
})
export class CompetitionService
{
    private competitionRepository: CompetitionRepository;
    private sitecoreWinners: PriceWinners[];
    private window: Window;

    public constructor(competitionRepository: CompetitionRepository, @Inject(DOCUMENT) private document: Document)
    {
        this.competitionRepository = competitionRepository;
        this.window = this.document.defaultView;
/* */
        if(this.window["ddkCompetitions"]){
            const competitionsArray = this.window["ddkCompetitions"];
            const PrizeWinners = [];

            competitionsArray.forEach((item, index) => {

                if(item.Prizes && item.Prizes.length > 0){
                    item.Prizes.forEach( (prize, prizeIndex) => {

                        const prizeWinners = prize.Winners == null ? [] : prize.Winners;

                        prizeWinners.forEach( (winner) =>{
                            winner.firstName = winner.FirstName;
                            winner.lastName = winner.LastName;
                            winner.city = winner.City;
                        })

                        PrizeWinners.push(
                            {
                                competitionId: parseInt(item.CompetitionId),
                                prize: {
                                    prizeId: prize.PrizeId,
                                    name: prize.Name,
                                    description: prize.Description
                                },
                                winners: prizeWinners
                            }
    
                        )
                    
                    })
                }
            });
            
            
            this.sitecoreWinners = PrizeWinners;

        }else{
            this.sitecoreWinners = [];
        }

    }

    public getCompetitions(): Observable<Competition[]>
    {
        return this.competitionRepository.getCompetitions();
    }

    public getCompetitionById(competitionId: number): Observable<Competition>
    {
        return this.competitionRepository.getCompetitions().pipe(
            map(competitions =>
            {
                const competition = competitions.filter(item => item.Id === competitionId)[0];
                return competition;
            })
        );
    }

    public getCurrentCompetitions(): Observable<Competition[]>
    {
        const nowMillis = new Date().getTime();
        return this.competitionRepository.getCompetitions().pipe(
            map(competitions =>
            {
                const activeCompetitions = competitions.filter(item =>
                {
                    const fromDateMillis = new Date(item.ActiveFrom).getTime();
                    const toDateMillis = new Date(item.ActiveTo).getTime();

                    return (fromDateMillis < nowMillis) && (nowMillis < toDateMillis);
                });
                return activeCompetitions;
            })
        );
    }

    public getPastCompetitions(): Observable<Competition[]>
    {
        const nowMillis = new Date().getTime();
        return this.competitionRepository.getCompetitions().pipe(
            map(competitions =>
            {
                const activeCompetitions = competitions.filter(item =>
                {
                    const toDateMillis = new Date(item.ActiveTo).getTime();

                    return nowMillis > toDateMillis;
                });
                return activeCompetitions;
            })
        );
    }

    public signUpToCompetition(competitor: SimpleCompetitor): Observable<number>
    {
        return this.competitionRepository.signUpToCompetition(competitor);
    }

    public getWinners(competitionId: number): Observable<CompetitionPrizeWinnersGroup[]>
    {
        let winners = this.getWinnersInFileByCompetitionId(competitionId);

        if (winners != null && winners.length > 0)
        {
            return of(winners);
        }


        const umbracoContentWinners: CompetitionWinnersContentResponse = {
            Winners: [],
            OrderedPrizes: []
        }

        return forkJoin([
            this.getWinnersByCompetitionId(competitionId),
            this.getPrizesByCompetitionId(competitionId)
        ]).pipe(map(([winners, prizes]) => {

            const umbracoContentPrizes = umbracoContentWinners.OrderedPrizes;

            // sort prizes by order on umbraco content winners
            // Even if no winners are registered in Umbraco, having the prizes listed will let them act as sorting sequence.
            prizes.sort((p1, p2) => { 
                return umbracoContentPrizes.findIndex(p => p.Id === p1.Id) - umbracoContentPrizes.findIndex(p => p.Id === p2.Id); 
            });
            
            // Merge winners from different sources into single list.
            // Winners from Umbraco are merged with winners listed in Oracle.
            winners = [...winners, ... umbracoContentWinners.Winners];            

            // Group winners by prizes.
            const prizeGroups = prizes.map(p => {
                return {
                    prize: p,
                    winners: winners.filter(w => w.PrizeId === p.Id)
                }
            });
            // Return only prizes with winners if winner(s) exist(s).
            return prizeGroups.filter(pg => pg.winners.length > 0);
        }));
    }

    private getPrizesByCompetitionId(competitionId): Observable<CompetitionPrize[]>
    {
        return this.competitionRepository.getPrizesByCompetitionId(competitionId);
    }

    /**
     * Gets list of winners by competition id from json text file.
     * @param competitionId 
     * @returns a list of grouped winners by prize.
     */
    private getWinnersInFileByCompetitionId(competitionId: number): CompetitionPrizeWinnersGroup[]
    {
        const winnerGroupForCompetition = this.sitecoreWinners.filter(o => o.competitionId === competitionId);

        let winnerGroups = [];
        winnerGroupForCompetition.forEach(wg => {
            winnerGroups.push( {
                prize: {
                    CompetitionId: competitionId,
                    Description: wg.prize.description,
                    Id: wg.prize.prizeId
                },
                winners: wg.winners.map(
                    w => {
                        return {
                            Name: `${w.firstName} ${w.lastName}`,
                            City: `${w.city}`,
                            CompetitionId: competitionId,
                            PrizeId: wg.prize.prizeId
                        }         
                    }
                )
            });
        });
        return winnerGroups;
    }

    private getWinnersByCompetitionId(competitionId: number): Observable<CompetitionWinner[]>
    {
        return this.competitionRepository.getWinnersByCompetitionId(competitionId);
    }
}
